// Living radial "constellation" memory graph (canvas), fed by REAL CogniMemo data.
//
// ORG scope   → company core → people (avatars) → each person's memory clusters
//               (the 8 fact types) with memory dots, connected apps on the outer
//               skin, and a faint cross-person web (same-type memories linked).
// PERSONAL    → "You" at the centre → the 8 fact-type clusters → topics/entities
//               (derived from a memory's department / tool) → apps skin.
//
// Everything maps 1:1 to data we already have: Clerk id / org id, CogniMemo
// fact_type + memories, orgMembers, Composio connectors. Topics are derived
// client-side until CogniMemo entity extraction is dense; agents are behind a
// toggle until that layer exists. Click any node → a provenance detail card.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { resolveContextToolIcon } from "../context/contextToolIcon";
import { resolvePersonAvatarUrl } from "../workspace/morrowPersonAvatarCatalog";
import type { MorrowMemory } from "../../morrowMemory";
import type { OrgMember } from "../../orgMembers";
import { FACT_TYPE_META, factTypeColor } from "./forceGraphModel";

type Scope = "org" | "personal";
type Kind = "company" | "you" | "person" | "kind" | "mem" | "app" | "entity" | "agent" | "aenv";

interface CNode {
  id: string;
  t: Kind;
  x: number;
  y: number;
  r: number;
  color: string;
  label?: string;
  name?: string;
  imageUrl?: string;
  factType?: string;
  owner?: string | null;
  memory?: MorrowMemory;
  phase?: number;
  hidden?: boolean;
}
interface CLink {
  a: CNode;
  b: CNode;
  factType?: string;
  flow?: boolean;
  ref?: boolean;
  cross?: boolean;
  about?: boolean;
  skin?: boolean;
  agent?: boolean;
  hidden?: boolean;
}

const TYPE_LABEL = new Map(FACT_TYPE_META.map((f) => [f.type, f.label]));
const factLabel = (t?: string | null) =>
  TYPE_LABEL.get((t ?? "").trim().toLowerCase()) ?? (t ? t : "Memory");
const ORDER = FACT_TYPE_META.map((f) => f.type);

// Composio slug → brand colour + one-letter fallback when the logo is unavailable.
const APP_META: ReadonlyArray<{ re: RegExp; c: string; l: string; name: string }> = [
  { re: /gmail|google_?mail/, c: "#ea4335", l: "G", name: "Gmail" },
  { re: /calendar/, c: "#4285f4", l: "C", name: "Calendar" },
  { re: /drive|docs|sheets/, c: "#2563eb", l: "D", name: "Drive" },
  { re: /slack/, c: "#611f69", l: "S", name: "Slack" },
  { re: /notion/, c: "#0f172a", l: "N", name: "Notion" },
  { re: /linkedin/, c: "#0a66c2", l: "in", name: "LinkedIn" },
  { re: /whatsapp/, c: "#25d366", l: "W", name: "WhatsApp" },
  { re: /github/, c: "#111827", l: "GH", name: "GitHub" },
];
const appMeta = (slug: string) => {
  const s = slug.toLowerCase();
  return (
    APP_META.find((a) => a.re.test(s)) ?? {
      c: "#64748b",
      l: (slug[0] ?? "?").toUpperCase(),
      name: slug,
    }
  );
};

const slug = (v: string) =>
  v
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-");
const emailKey = (v?: string | null) => (v ?? "").trim().toLowerCase();
const topicOf = (m: MorrowMemory): string | null =>
  m.department?.trim() || (m.toolSlug ? appMeta(m.toolSlug).name : null);

type ImageCacheEntry = HTMLImageElement | "pending" | "failed";

function clipCircle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.clip();
}

function clipRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  s: number,
  rad: number,
) {
  const left = x - s;
  const top = y - s;
  const w = s * 2;
  const h = s * 2;
  ctx.beginPath();
  ctx.moveTo(left + rad, top);
  ctx.arcTo(left + w, top, left + w, top + h, rad);
  ctx.arcTo(left + w, top + h, left, top + h, rad);
  ctx.arcTo(left, top + h, left, top, rad);
  ctx.arcTo(left, top, left + w, top, rad);
  ctx.closePath();
  ctx.clip();
}

function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  radius: number,
) {
  const size = radius * 2;
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) {
    ctx.drawImage(img, x - radius, y - radius, size, size);
    return;
  }
  const scale = Math.max(size / iw, size / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  ctx.drawImage(img, x - dw / 2, y - dh / 2, dw, dh);
}

function drawableImage(entry: ImageCacheEntry | undefined): HTMLImageElement | null {
  if (!(entry instanceof HTMLImageElement) || !entry.complete) return null;
  return entry;
}

function buildModel(input: {
  scope: Scope;
  rootName: string;
  rootLogoUrl?: string;
  members: readonly OrgMember[];
  memories: readonly MorrowMemory[];
  connectedTools: readonly string[];
  resolvePersonAvatar: (member: OrgMember) => string;
  base: number;
  showAgents: boolean;
}): { nodes: CNode[]; links: CLink[] } {
  const {
    scope,
    rootName,
    rootLogoUrl,
    members,
    memories,
    connectedTools,
    base,
    resolvePersonAvatar,
  } = input;
  const nodes: CNode[] = [];
  const links: CLink[] = [];
  const N = (n: CNode) => (nodes.push(n), n);
  const L = (l: CLink) => links.push(l);

  const clusterFor = (
    parent: CNode,
    ownerLabel: string | null,
    mems: readonly MorrowMemory[],
    r1: number,
    baseAngle: number,
    spread: number,
  ) => {
    const byType = new Map<string, MorrowMemory[]>();
    for (const m of mems) {
      const k = (m.type ?? "").trim().toLowerCase() || "world";
      (byType.get(k) ?? byType.set(k, []).get(k)!).push(m);
    }
    const kinds = ORDER.filter((k) => byType.has(k));
    kinds.forEach((kind, ki) => {
      const ca = baseAngle + (ki - (kinds.length - 1) / 2) * (spread / Math.max(kinds.length, 1));
      const cd = r1;
      const hx = parent.x + Math.cos(ca) * cd;
      const hy = parent.y + Math.sin(ca) * cd;
      const hub = N({
        id: `${parent.id}:${kind}`,
        t: "kind",
        x: hx,
        y: hy,
        r: 6,
        color: factTypeColor(kind),
        label: kind,
        owner: ownerLabel,
      });
      L({ a: parent, b: hub, flow: true, factType: kind });
      const dots = byType.get(kind)!;
      dots.forEach((m, k) => {
        const da = ca + (k - dots.length / 2) * 0.22;
        const dd = base * 0.05 + ((k * 7) % 22);
        L({
          a: hub,
          b: N({
            id: `mem:${m.id}`,
            t: "mem",
            x: hx + Math.cos(da) * dd,
            y: hy + Math.sin(da) * dd,
            r: 2.6 + (k % 3) * 0.6,
            color: factTypeColor(kind),
            factType: kind,
            owner: ownerLabel,
            memory: m,
            phase: (m.id.charCodeAt(0) % 60) / 10,
          }),
        });
      });
    });
  };

  if (scope === "org") {
    const company = N({
      id: "company",
      t: "company",
      x: 0,
      y: 0,
      r: 24,
      color: "#0f172a",
      label: (rootName[0] ?? "•").toUpperCase(),
      name: rootName,
      ...(rootLogoUrl ? { imageUrl: rootLogoUrl } : {}),
    });
    const joined = members.filter((m) => m.joined);
    const Rp = base * 0.3;
    const personByEmail = new Map<string, CNode>();
    joined.forEach((m, i) => {
      const a = (i / Math.max(joined.length, 1)) * Math.PI * 2 - Math.PI / 2;
      const p = N({
        id: `person:${slug(m.email)}`,
        t: "person",
        x: Math.cos(a) * Rp,
        y: Math.sin(a) * Rp,
        r: 14,
        color: "#6366f1",
        label: (m.name || m.email).slice(0, 2).toUpperCase(),
        name: m.name || m.email,
        imageUrl: resolvePersonAvatar(m),
        phase: i,
      });
      personByEmail.set(emailKey(m.email), p);
      L({ a: company, b: p, flow: true });
      const mine = memories.filter((mm) => emailKey(mm.ownerEmail) === emailKey(m.email));
      clusterFor(p, m.email, mine, base * 0.085, a, 1.3);
    });
    // org-shared / unowned memories cluster around the company core itself
    const shared = memories.filter((mm) => !personByEmail.has(emailKey(mm.ownerEmail)));
    clusterFor(company, null, shared, base * 0.12, -Math.PI / 2, Math.PI * 2 * 0.9);
    // apps on the outer skin (caller's connectors → org-level for now; per-member is a gap)
    connectedTools.slice(0, 12).forEach((s, i) => {
      const a = (i / Math.max(connectedTools.length, 1)) * Math.PI * 2 - Math.PI / 2;
      const meta = appMeta(s);
      const toolIcon = resolveContextToolIcon(s);
      const an = N({
        id: `app:${s}`,
        t: "app",
        x: Math.cos(a) * base * 0.44,
        y: Math.sin(a) * base * 0.44,
        r: 9,
        color: meta.c,
        label: meta.l,
        name: toolIcon.appName ?? meta.name,
        ...(toolIcon.logoUrl ? { imageUrl: toolIcon.logoUrl } : {}),
      });
      L({ a: company, b: an, skin: true });
    });
    // cross-person web: same-type memories across different owners
    const memNodes = nodes.filter((n) => n.t === "mem");
    const byType = new Map<string, CNode[]>();
    for (const n of memNodes)
      (byType.get(n.factType!) ?? byType.set(n.factType!, []).get(n.factType!)!).push(n);
    for (const list of byType.values()) {
      for (const n of list) {
        if (n.memory!.id.charCodeAt(1) % 10 < 4) {
          const other = list[(list.indexOf(n) + 3) % list.length];
          if (other && other.owner !== n.owner)
            L({
              a: n,
              b: other,
              ref: true,
              cross: true,
              ...(n.factType ? { factType: n.factType } : {}),
            });
        }
      }
    }
  } else {
    const you = N({
      id: "you",
      t: "you",
      x: 0,
      y: 0,
      r: 26,
      color: "#4338ca",
      label: "You",
      name: "You",
      ...(rootLogoUrl ? { imageUrl: rootLogoUrl } : {}),
    });
    clusterFor(you, "You", memories, base * 0.215, -Math.PI / 2, Math.PI * 2 * 0.94);
    // derived topics ring — from department / tool
    const topics = Array.from(new Set(memories.map(topicOf).filter(Boolean) as string[]));
    const entNodes = new Map<string, CNode>();
    topics.slice(0, 8).forEach((t, i) => {
      const a = (i / Math.max(topics.length, 1)) * Math.PI * 2 - Math.PI / 2 + 0.3;
      entNodes.set(
        t,
        N({
          id: `entity:${slug(t)}`,
          t: "entity",
          x: Math.cos(a) * base * 0.36,
          y: Math.sin(a) * base * 0.36,
          r: 12,
          color: "#334155",
          label: t,
          name: t,
        }),
      );
    });
    for (const n of nodes.filter((x) => x.t === "mem")) {
      const t = n.memory ? topicOf(n.memory) : null;
      const e = t ? entNodes.get(t) : null;
      if (e) L({ a: n, b: e, ref: true, about: true });
    }
    connectedTools.slice(0, 12).forEach((s, i) => {
      const a = (i / Math.max(connectedTools.length, 1)) * Math.PI * 2 - Math.PI / 2 + 0.2;
      const meta = appMeta(s);
      const toolIcon = resolveContextToolIcon(s);
      L({
        a: you,
        b: N({
          id: `app:${s}`,
          t: "app",
          x: Math.cos(a) * base * 0.46,
          y: Math.sin(a) * base * 0.46,
          r: 9,
          color: meta.c,
          label: meta.l,
          name: toolIcon.appName ?? meta.name,
          ...(toolIcon.logoUrl ? { imageUrl: toolIcon.logoUrl } : {}),
        }),
        skin: true,
      });
    });
  }
  return { nodes, links };
}

export interface ConstellationSelection {
  node: CNode;
  neighbors: CNode[];
}

export function MemoryConstellation({
  scope,
  rootName,
  rootLogoUrl,
  members,
  memories,
  connectedTools,
  resolvePersonAvatar,
  onSelect,
  selectedId,
}: {
  readonly scope: Scope;
  readonly rootName: string;
  readonly rootLogoUrl?: string;
  readonly members: readonly OrgMember[];
  readonly memories: readonly MorrowMemory[];
  readonly connectedTools: readonly string[];
  readonly resolvePersonAvatar?: (member: OrgMember) => string;
  readonly onSelect?: (sel: ConstellationSelection | null) => void;
  readonly selectedId?: string | null;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modelRef = useRef<{ nodes: CNode[]; links: CLink[] }>({ nodes: [], links: [] });
  const nbrRef = useRef<Map<CNode, Set<CNode>>>(new Map());
  const idMapRef = useRef<Map<string, CNode>>(new Map());
  const imageCacheRef = useRef<Map<string, ImageCacheEntry>>(new Map());
  const viewRef = useRef({
    angle: 0,
    spin: true,
    dragging: false,
    lastX: 0,
    moved: 0,
    w: 0,
    h: 0,
    dpr: 1,
  });
  const selRef = useRef<string | null>(selectedId ?? null);
  const [spin, setSpin] = useState(true);

  const avatarForMember = useCallback(
    (member: OrgMember) => {
      if (resolvePersonAvatar) return resolvePersonAvatar(member);
      const name = member.name?.trim() || member.email;
      return resolvePersonAvatarUrl(name, {
        ...(member.avatarSeed ? { avatarSeed: member.avatarSeed } : {}),
      });
    },
    [resolvePersonAvatar],
  );

  const preloadImage = useCallback((url: string | undefined) => {
    if (!url) return;
    const cache = imageCacheRef.current;
    const existing = cache.get(url);
    if (existing instanceof HTMLImageElement || existing === "pending") return;
    if (existing === "failed") cache.delete(url);
    cache.set(url, "pending");
    const img = new Image();
    // logos.composio.dev omits CORS headers — crossOrigin="anonymous" blocks canvas draw.
    try {
      if (new URL(url, window.location.href).origin === window.location.origin) {
        img.crossOrigin = "anonymous";
      }
    } catch {
      // ignore malformed URLs
    }
    img.onload = () => {
      cache.set(url, img);
    };
    img.onerror = () => {
      cache.set(url, "failed");
    };
    img.src = url;
  }, []);

  useEffect(() => {
    selRef.current = selectedId ?? null;
  }, [selectedId]);

  const rebuild = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    const base = Math.min(w, h);
    const model = buildModel({
      scope,
      rootName,
      ...(rootLogoUrl ? { rootLogoUrl } : {}),
      members,
      memories,
      connectedTools,
      resolvePersonAvatar: avatarForMember,
      base,
      showAgents: false,
    });
    modelRef.current = model;
    for (const node of model.nodes) preloadImage(node.imageUrl);
    const nbr = new Map<CNode, Set<CNode>>();
    const add = (a: CNode, b: CNode) => (nbr.get(a) ?? nbr.set(a, new Set()).get(a)!).add(b);
    for (const l of model.links) {
      add(l.a, l.b);
      add(l.b, l.a);
    }
    nbrRef.current = nbr;
    idMapRef.current = new Map(model.nodes.map((n) => [n.id, n]));
    viewRef.current.w = w;
    viewRef.current.h = h;
  }, [
    scope,
    rootName,
    rootLogoUrl,
    members,
    memories,
    connectedTools,
    avatarForMember,
    preloadImage,
  ]);

  useEffect(() => {
    rebuild();
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => rebuild());
    ro.observe(el);
    return () => ro.disconnect();
  }, [rebuild]);

  // resize canvas backing store
  const resize = useCallback(() => {
    const el = wrapRef.current;
    const cv = canvasRef.current;
    if (!el || !cv) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    viewRef.current.dpr = dpr;
    viewRef.current.w = el.clientWidth;
    viewRef.current.h = el.clientHeight;
    cv.width = el.clientWidth * dpr;
    cv.height = el.clientHeight * dpr;
  }, []);
  useEffect(() => {
    resize();
    const ro = new ResizeObserver(resize);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [resize]);

  useEffect(() => {
    viewRef.current.spin = spin;
  }, [spin]);

  // animation loop
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    let raf = 0;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) viewRef.current.spin = false;

    const rrect = (x: number, y: number, s: number, rad: number, col: string) => {
      ctx.beginPath();
      ctx.moveTo(x - s + rad, y - s);
      ctx.arcTo(x + s, y - s, x + s, y + s, rad);
      ctx.arcTo(x + s, y + s, x - s, y + s, rad);
      ctx.arcTo(x - s, y + s, x - s, y - s, rad);
      ctx.arcTo(x - s, y - s, x + s, y - s, rad);
      ctx.closePath();
      ctx.fillStyle = col;
      ctx.fill();
    };

    const frame = (now: number) => {
      const v = viewRef.current;
      const { nodes, links } = modelRef.current;
      const dpr = v.dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, v.w, v.h);
      const base = Math.min(v.w, v.h);
      ctx.save();
      ctx.translate(v.w / 2, v.h / 2);
      const co = Math.cos(v.angle);
      const si = Math.sin(v.angle);
      const R = (x: number, y: number) => ({ x: x * co - y * si, y: x * si + y * co });
      const sel = selRef.current ? (idMapRef.current.get(selRef.current) ?? null) : null;
      const nb = sel ? nbrRef.current.get(sel) : null;
      const lit = (n: CNode) => !sel || n === sel || (nb ? nb.has(n) : false);

      // guide rings
      ctx.lineWidth = 1;
      [0.3, 0.44].forEach((f, i) => {
        ctx.strokeStyle = `rgba(148,163,184,${0.16 + i * 0.04})`;
        ctx.beginPath();
        ctx.arc(0, 0, base * f, 0, Math.PI * 2);
        ctx.stroke();
      });

      // ref links (curved)
      for (const l of links) {
        if (l.hidden || !l.ref) continue;
        const hot = sel && (l.a === sel || l.b === sel);
        if (sel && !hot) continue;
        const A = R(l.a.x, l.a.y);
        const B = R(l.b.x, l.b.y);
        ctx.strokeStyle = hot
          ? l.factType
            ? factTypeColor(l.factType)
            : "#4338ca"
          : "rgba(148,163,184,0.13)";
        ctx.lineWidth = hot ? 1.6 : 0.7;
        ctx.beginPath();
        ctx.moveTo(A.x, A.y);
        ctx.quadraticCurveTo(((A.x + B.x) / 2) * 0.6, ((A.y + B.y) / 2) * 0.6, B.x, B.y);
        ctx.stroke();
      }
      // tree links
      for (const l of links) {
        if (l.hidden || l.ref) continue;
        const A = R(l.a.x, l.a.y);
        const B = R(l.b.x, l.b.y);
        const on = lit(l.a) && lit(l.b);
        ctx.strokeStyle = l.flow ? "rgba(99,102,241,0.28)" : "rgba(148,163,184,0.22)";
        ctx.globalAlpha = sel ? (on ? 1 : 0.14) : 1;
        ctx.lineWidth = l.flow ? 1 : 0.7;
        ctx.beginPath();
        ctx.moveTo(A.x, A.y);
        ctx.lineTo(B.x, B.y);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      const order: Record<Kind, number> = {
        mem: 0,
        aenv: 0,
        kind: 1,
        entity: 2,
        app: 2,
        agent: 2,
        person: 3,
        company: 4,
        you: 4,
      };
      [...nodes]
        .sort((a, b) => order[a.t] - order[b.t])
        .forEach((n) => {
          if (n.hidden) return;
          const P = R(n.x, n.y);
          const on = lit(n);
          const dim = sel && !on;
          const pulse = n.phase != null ? 0.5 + 0.5 * Math.sin(now / 650 + n.phase) : 1;
          ctx.globalAlpha = dim ? 0.15 : 1;
          if (n.t === "mem" || n.t === "aenv") {
            ctx.beginPath();
            ctx.arc(P.x, P.y, n === sel ? n.r + 2 : n.r, 0, Math.PI * 2);
            ctx.fillStyle = n.color;
            ctx.globalAlpha = dim ? 0.15 : 0.55 + 0.45 * pulse;
            ctx.fill();
            if (n === sel) {
              ctx.globalAlpha = 1;
              ctx.lineWidth = 1.5;
              ctx.strokeStyle = n.color;
              ctx.stroke();
            }
            ctx.globalAlpha = 1;
            return;
          }
          if (n.t === "kind") {
            ctx.beginPath();
            ctx.arc(P.x, P.y, n.r + 3 + 2 * pulse, 0, Math.PI * 2);
            ctx.fillStyle = n.color + "22";
            ctx.fill();
            ctx.beginPath();
            ctx.arc(P.x, P.y, n.r, 0, Math.PI * 2);
            ctx.fillStyle = n.color;
            ctx.fill();
            if (scope === "personal") {
              ctx.fillStyle = dim ? "#cbd5e1" : "#334155";
              ctx.font = `600 9.5px ui-sans-serif, system-ui, sans-serif`;
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(factLabel(n.label), P.x, P.y - n.r - 8);
            }
            ctx.globalAlpha = 1;
            return;
          }
          const cachedImage = n.imageUrl
            ? drawableImage(imageCacheRef.current.get(n.imageUrl))
            : null;

          ctx.save();
          ctx.shadowColor = "rgba(15,23,42,0.22)";
          ctx.shadowBlur = 9;
          ctx.shadowOffsetY = 1;
          if (n.t === "app") {
            rrect(P.x, P.y, n.r, 4, cachedImage ? "#ffffff" : n.color);
          } else if (n.t === "company" || n.t === "you") {
            rrect(P.x, P.y, n.r, 8, cachedImage ? "#ffffff" : n.color);
          } else {
            ctx.beginPath();
            ctx.arc(P.x, P.y, n.r, 0, Math.PI * 2);
            ctx.fillStyle = cachedImage ? "#e2e8f0" : n.color;
            ctx.fill();
          }
          ctx.restore();

          if (cachedImage) {
            ctx.save();
            if (n.t === "person") {
              clipCircle(ctx, P.x, P.y, n.r - 0.5);
            } else if (n.t === "app") {
              clipRoundRect(ctx, P.x, P.y, n.r - 1, 4);
            } else if (n.t === "company" || n.t === "you") {
              clipRoundRect(ctx, P.x, P.y, n.r - 1, 8);
            }
            drawImageCover(ctx, cachedImage, P.x, P.y, n.r - 1);
            ctx.restore();
          }

          if (n.t === "app") {
            ctx.beginPath();
            ctx.moveTo(P.x - n.r + 4, P.y - n.r);
            ctx.arcTo(P.x + n.r, P.y - n.r, P.x + n.r, P.y + n.r, 4);
            ctx.arcTo(P.x + n.r, P.y + n.r, P.x - n.r, P.y + n.r, 4);
            ctx.arcTo(P.x - n.r, P.y + n.r, P.x - n.r, P.y - n.r, 4);
            ctx.arcTo(P.x - n.r, P.y - n.r, P.x + n.r, P.y - n.r, 4);
            ctx.closePath();
          } else if (n.t === "company" || n.t === "you") {
            ctx.beginPath();
            ctx.moveTo(P.x - n.r + 8, P.y - n.r);
            ctx.arcTo(P.x + n.r, P.y - n.r, P.x + n.r, P.y + n.r, 8);
            ctx.arcTo(P.x + n.r, P.y + n.r, P.x - n.r, P.y + n.r, 8);
            ctx.arcTo(P.x - n.r, P.y + n.r, P.x - n.r, P.y - n.r, 8);
            ctx.arcTo(P.x - n.r, P.y - n.r, P.x + n.r, P.y - n.r, 8);
            ctx.closePath();
          } else {
            ctx.beginPath();
            ctx.arc(P.x, P.y, n.r, 0, Math.PI * 2);
          }
          if (n === sel) {
            ctx.lineWidth = 2.5;
            ctx.strokeStyle = n.color;
            ctx.stroke();
          } else if (n.t === "person" || n.t === "you" || n.t === "entity") {
            ctx.lineWidth = 2;
            ctx.strokeStyle = "#fff";
            ctx.stroke();
          }

          if (!cachedImage) {
            ctx.fillStyle = "#fff";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.font = `700 ${n.t === "company" || n.t === "you" ? 13 : 10}px ui-sans-serif, system-ui, sans-serif`;
            if (n.t === "company" || n.t === "you" || n.t === "app")
              ctx.fillText(n.label ?? "", P.x, P.y + 0.5);
            if (n.t === "person") ctx.fillText(n.label ?? "", P.x, P.y + 0.5);
          }
          if (n.t === "person") {
            ctx.fillStyle = dim ? "#cbd5e1" : "#334155";
            ctx.font = `600 10px ui-sans-serif, system-ui, sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(n.name ?? "", P.x, P.y + n.r + 9);
          }
          if (n.t === "entity") {
            ctx.fillStyle = dim ? "#cbd5e1" : "#334155";
            ctx.font = `600 10px ui-sans-serif, system-ui, sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(n.name ?? "", P.x, P.y + n.r + 9);
          }
          ctx.globalAlpha = 1;
        });
      ctx.restore();
      if (v.spin && !v.dragging && !sel) v.angle += 0.0012;
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [scope]);

  // pointer: drag to spin, click to select
  const pick = useCallback((clientX: number, clientY: number): CNode | null => {
    const cv = canvasRef.current;
    if (!cv) return null;
    const rect = cv.getBoundingClientRect();
    const v = viewRef.current;
    const mx = clientX - rect.left - v.w / 2;
    const my = clientY - rect.top - v.h / 2;
    const co = Math.cos(v.angle);
    const si = Math.sin(v.angle);
    let best: CNode | null = null;
    let bd = 99;
    for (const n of modelRef.current.nodes) {
      if (n.hidden) continue;
      const rx = n.x * co - n.y * si;
      const ry = n.x * si + n.y * co;
      const d = Math.hypot(mx - rx, my - ry);
      if (d < n.r + 5 && d < bd) {
        bd = d;
        best = n;
      }
    }
    return best;
  }, []);

  const onDown = (e: React.PointerEvent) => {
    const v = viewRef.current;
    v.dragging = true;
    v.lastX = e.clientX;
    v.moved = 0;
  };
  const onMove = (e: React.PointerEvent) => {
    const v = viewRef.current;
    if (v.dragging) {
      v.moved += Math.abs(e.clientX - v.lastX);
      v.angle += (e.clientX - v.lastX) * 0.005;
      v.lastX = e.clientX;
    }
  };
  const onUp = (e: React.PointerEvent) => {
    const v = viewRef.current;
    v.dragging = false;
    if (v.moved < 5) {
      const n = pick(e.clientX, e.clientY);
      if (!n || n.t === "aenv") {
        onSelect?.(null);
      } else {
        onSelect?.({ node: n, neighbors: [...(nbrRef.current.get(n) ?? [])] });
      }
    }
  };

  const stats = useMemo(() => {
    const people = members.filter((m) => m.joined).length;
    return { count: memories.length, people };
  }, [members, memories]);

  return (
    <div
      ref={wrapRef}
      className="relative h-full w-full overflow-hidden rounded-2xl border border-border bg-gradient-to-b from-white to-slate-100 shadow-inner"
    >
      <canvas
        ref={canvasRef}
        className="block h-full w-full cursor-grab active:cursor-grabbing"
        style={{ width: "100%", height: "100%" }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={() => (viewRef.current.dragging = false)}
      />
      <div className="pointer-events-none absolute left-4 top-4 text-xs text-slate-500">
        {scope === "org" ? (
          <>
            Morrow remembers <b className="tabular-nums text-slate-800">{stats.count}</b> things
            across <b className="tabular-nums text-slate-800">{stats.people}</b> people
          </>
        ) : (
          <>
            Morrow remembers <b className="tabular-nums text-slate-800">{stats.count}</b> things
            about you
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() => setSpin((s) => !s)}
        className="absolute right-3 top-3 rounded-lg border border-border bg-white/85 px-2.5 py-1 text-xs text-slate-600 backdrop-blur hover:text-slate-900"
      >
        {spin ? "Motion: on" : "Motion: off"}
      </button>
      {memories.length === 0 ? (
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-sm text-slate-400">
          No memories yet — they’ll appear here as Morrow learns.
        </div>
      ) : null}
    </div>
  );
}
